/**
 * AuthLayoutRoute — the shell for sign-in, register and password recovery.
 *
 * Dressed as the rest of the site rather than a stock admin kit: the drifting
 * dust from the About page, the header's logo and type, a display headline,
 * and the form on a glass panel. It still offers no site navigation, only a
 * way back, because a sign-in screen has nowhere else to send you.
 *
 * Below `lg` the headline shrinks above the panel instead of sitting beside
 * it, so the email field stays near the top of a phone screen.
 */
import { Link, Outlet } from 'react-router-dom';
import { brand } from './brand';
import { Backdrop, Mark, ThemePill, eyebrow, headerFont } from './SiteChrome';

const outline = 'block text-transparent [-webkit-text-stroke:2px_var(--color-primary)]';

export function AuthLayoutRoute() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      <Backdrop />

      <div className="relative z-[1] flex min-h-screen flex-col">
        <header className="mx-auto flex w-full max-w-[1440px] items-center justify-between gap-4 px-[clamp(20px,4vw,64px)] py-4">
          <Link to="/" className="flex items-center gap-2.5 text-lg font-bold tracking-[-.01em]">
            <Mark />
            {brand.name}
          </Link>
          <div className="flex items-center gap-2.5">
            <ThemePill />
            <Link
              to="/"
              className={`${headerFont} flex h-10 items-center gap-2 rounded-full border border-line px-4 text-[15px] transition-colors hover:border-primary`}
            >
              ← <span className="max-[419px]:hidden">Back to site</span>
            </Link>
          </div>
        </header>

        <main className="mx-auto grid w-full max-w-[1440px] flex-1 items-center gap-10 px-[clamp(20px,4vw,64px)] pb-16 pt-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,460px)] lg:gap-20">
          <div className="flex flex-col gap-5">
            <div className={`${eyebrow} flex items-center gap-2.5 text-primary`}>
              <span className="vc-pulse size-2 rounded-full bg-primary" />[ STUDIO ADMIN ]
            </div>
            <h1 className="m-0 font-display text-[clamp(48px,7vw,112px)] font-black uppercase leading-[.84] tracking-normal">
              Run the site
              <span className={outline}>from one place.</span>
            </h1>
            <p className="m-0 max-w-[440px] text-[clamp(15px,1.4vw,17px)] leading-[1.6] text-soft max-lg:hidden">
              The team, the case studies and the services on {brand.name}.com, edited here and live the moment you save.
            </p>
            <div className={`${eyebrow} flex flex-wrap gap-2 text-muted-foreground max-lg:hidden`}>
              {['TEAM', 'PROJECTS', 'SERVICES'].map((t) => (
                <span key={t} className="rounded-full border border-line-strong bg-glass px-3 py-1.5">{t}</span>
              ))}
            </div>
          </div>

          <div className="relative overflow-hidden rounded-[28px] border border-line bg-glass p-[clamp(24px,3vw,40px)] shadow-[0_40px_120px_-40px_rgba(94,25,236,.55)] backdrop-blur-[14px]">
            <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 size-[260px] rounded-full bg-primary/20 blur-[80px]" />
            <div className="relative">
              {/* The page supplies its own heading and form. */}
              <Outlet />
            </div>
          </div>
        </main>

        <footer className={`${eyebrow} mx-auto w-full max-w-[1440px] px-[clamp(20px,4vw,64px)] py-5 text-subtle`}>
          © {new Date().getFullYear()} {brand.legalName}
        </footer>
      </div>
    </div>
  );
}
